import { useCallback, useRef } from "react";
import { useAppDispatch } from "../store/hooks";
import { pushActivity, setStatusText } from "../store/slices/copilotSlice";
import { apiUrl } from "../lib/utils";

/**
 * Resumes a deep analysis whose live run was lost (page refresh / closed
 * browser between MCAP approval and upload completion). Consumes the same
 * analysis stream headlessly — progress lands in the activity log and the
 * findings are appended server-side onto the suspension turn — then invokes
 * `reload` so the updated message renders without a manual refresh.
 */
export function useResumeDeepAnalysis(
  tokenRef: React.RefObject<string | null | undefined>,
  activeThreadIdRef: React.RefObject<string | undefined>,
) {
  const dispatch = useAppDispatch();
  const resumingRef = useRef<Set<string>>(new Set());

  return useCallback(
    async (threadId: string, reload: () => Promise<void>) => {
      if (resumingRef.current.has(threadId)) return;
      resumingRef.current.add(threadId);
      dispatch(setStatusText("ANALYZING_MCAP"));
      dispatch(
        pushActivity({
          kind: "status",
          text: "Resuming MCAP deep analysis",
        }),
      );
      try {
        const res = await fetch(`${apiUrl}/v2/copilot/analysis-stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(tokenRef.current
              ? { Authorization: `Bearer ${tokenRef.current}` }
              : {}),
          },
          body: JSON.stringify({ thread_id: threadId }),
        });
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const raw = line.slice(6).trim();
            if (!raw || raw === "[DONE]") continue;
            try {
              const ev = JSON.parse(raw) as {
                type: string;
                status?: string;
                content?: string;
              };
              if (ev.type === "status" && ev.status) {
                dispatch(setStatusText(ev.status));
              } else if (ev.type === "reasoning" && ev.content) {
                for (const l of ev.content.split("\n")) {
                  if (l.trim())
                    dispatch(pushActivity({ kind: "tool", text: l.trim() }));
                }
              }
            } catch {
              /* malformed frame — skip */
            }
          }
        }
      } catch {
        /* analysis remains resumable on the next thread open */
      } finally {
        resumingRef.current.delete(threadId);
      }
      dispatch(setStatusText("IDLE"));
      // Only reload if the user is still looking at this conversation.
      if (activeThreadIdRef.current === threadId) {
        await reload();
      }
    },
    [dispatch, tokenRef, activeThreadIdRef],
  );
}
